import i18n from "../../i18n";
import type { DailyStats, LogEvent, LogType, UnitSystem } from "../types/domain";
import { toDateKey, splitMinutesByDay, toSleepDayKey } from "./date";

export const OZ_PER_ML = 1 / 29.5735;
export const G_PER_OZ = 28.3495;
export const CM_PER_INCH = 2.54;

export const TYPE_EMOJI: Record<LogType, string> = {
  feed: "🍼", diaper: "💩", sleep: "😴", weight: "⚖️",
  marker: "📍", vomiting: "🤢", hiccup: "😮‍💨", fever: "🌡️", pain: "😢", pump: "🤱",
  file: "📎", note: "📝", vaccine: "💉", task_completed: "✅"
};

export const VALID_LOG_TYPES: LogType[] = ["feed", "diaper", "sleep", "weight", "marker", "vomiting", "hiccup", "fever", "pain", "pump", "file", "note"];
export const HEALTH_CONDITION_TYPES: LogType[] = ["vomiting", "hiccup", "fever", "pain"];

export const initialStats: DailyStats = {
  date: "",
  feedTotalMl: 0,
  feedCount: 0,
  sleepCount: 0,
  diaperCountWet: 0,
  diaperCountSoiled: 0,
  alertsCount: 0
};

export function displayVolume(ml: number, unitSystem: UnitSystem): string {
  if (unitSystem === "imperial") return `${(ml * OZ_PER_ML).toFixed(1)} oz`;
  return `${ml} ml`;
}

export function displayWeight(g: number, unitSystem: UnitSystem): string {
  if (unitSystem === "imperial") {
    const totalOz = g / G_PER_OZ;
    const lbs = Math.floor(totalOz / 16);
    const oz = parseFloat((totalOz % 16).toFixed(1));
    return lbs > 0 ? `${lbs} lb ${oz} oz` : `${oz} oz`;
  }
  if (g >= 1000) return `${(g / 1000).toFixed(2)} kg`;
  return `${g} g`;
}

export function displayLength(cm: number, unitSystem: UnitSystem): string {
  if (unitSystem === "imperial") return `${(cm / CM_PER_INCH).toFixed(1)} in`;
  return `${cm} cm`;
}

export function formatDurationMin(totalMin: number): string {
  if (totalMin < 60) return `${totalMin} ${i18n.t("common.min")}`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

const LOG_TYPE_KEY: Record<LogType, string> = {
  feed: "form.feed",
  diaper: "form.diaper",
  sleep: "form.sleep",
  weight: "form.growth",
  marker: "form.marker",
  vomiting: "form.vomiting",
  hiccup: "form.hiccup",
  fever: "form.fever",
  pain: "form.pain",
  pump: "form.pump",
  file: "form.file",
  note: "form.note",
  vaccine: "form.vaccine",
  task_completed: "form.taskCompleted",
};

export function logTypeLabel(type: LogType): string {
  return i18n.t(LOG_TYPE_KEY[type]);
}

export function payloadSummary(item: LogEvent, unitSystem: UnitSystem = "metric"): string {
  const p = item.payload;
  switch (item.type) {
    case "feed":
      if (p.feedMethod === "nursing") return p.nursingSide
        ? i18n.t("payloadSummary.nursingSide", { side: i18n.t(`form.${p.nursingSide}`) })
        : i18n.t("payloadSummary.nursing");
      return p.amountMl ? displayVolume(p.amountMl, unitSystem) : "";
    case "diaper": {
      if (p.diaperKind === "soiled") {
        const parts: string[] = [i18n.t("logging.charts.soiled")];
        if (p.stoolColor) parts.push(i18n.t(`form.stool${p.stoolColor.charAt(0).toUpperCase() + p.stoolColor.slice(1)}`));
        if (p.stoolConsistency) parts.push(i18n.t(`form.stool${p.stoolConsistency.charAt(0).toUpperCase() + p.stoolConsistency.slice(1)}`));
        return parts.join(" · ");
      }
      return p.diaperKind ? i18n.t("logging.charts.wet") : "";
    }
    case "sleep":  return p.durationMin ? formatDurationMin(p.durationMin) : "";
    case "weight": {
      const parts: string[] = [];
      if (p.weightG) parts.push(displayWeight(p.weightG, unitSystem));
      if (p.lengthCm) parts.push(displayLength(p.lengthCm, unitSystem));
      return parts.join(" · ") || "";
    }
    case "marker": return p.markerText ?? "";
    case "note":   return "";
    case "file":   return p.fileName ?? (p.markerText ?? "");
    case "pump": {
      const parts = [
        p.pumpDurationMin ? formatDurationMin(p.pumpDurationMin) : null,
        p.pumpSide ? i18n.t(`form.${p.pumpSide}`) : null,
        p.amountMl ? displayVolume(p.amountMl, unitSystem) : null,
      ].filter(Boolean);
      return parts.join(" · ");
    }
    case "vaccine":        return (p.vaccineIds ?? []).join(", ");
    case "task_completed": return p.taskTitle ?? "";
    default:               return "";
  }
}

export function aggregateDay(items: LogEvent[], unitSystem: UnitSystem): { type: LogType; label: string }[] {
  const byType = new Map<LogType, LogEvent[]>();
  for (const item of items) {
    const arr = byType.get(item.type) ?? [];
    arr.push(item);
    byType.set(item.type, arr);
  }
  const result: { type: LogType; label: string }[] = [];
  for (const [type, typeItems] of byType) {
    const count = typeItems.length;
    let label = "";
    switch (type) {
      case "feed": {
        const nursingCount = typeItems.filter(i => i.payload.feedMethod === "nursing").length;
        const bottleItems = typeItems.filter(i => i.payload.feedMethod !== "nursing");
        const totalMl = bottleItems.reduce((s, i) => s + (i.payload.amountMl ?? 0), 0);
        const parts: string[] = [];
        if (bottleItems.length > 0) parts.push(totalMl > 0 ? `${bottleItems.length}× ${displayVolume(totalMl, unitSystem)}` : `${bottleItems.length}×`);
        if (nursingCount > 0) parts.push(i18n.t("aggregateDay.nursingCount", { count: nursingCount }));
        label = parts.join(", ");
        break;
      }
      case "diaper": {
        const wet = typeItems.filter(i => i.payload.diaperKind === "wet").length;
        const soiled = typeItems.filter(i => i.payload.diaperKind === "soiled").length;
        const parts: string[] = [];
        if (wet) parts.push(i18n.t("aggregateDay.wetCount", { count: wet }));
        if (soiled) parts.push(i18n.t("aggregateDay.soiledCount", { count: soiled }));
        label = parts.length ? parts.join(", ") : `${count}×`;
        break;
      }
      case "sleep": {
        const totalMin = typeItems.reduce((s, i) => s + (i.payload.durationMin ?? 0), 0);
        label = totalMin > 0 ? `${count}× · ${formatDurationMin(totalMin)}` : `${count}×`;
        break;
      }
      case "pump": {
        const totalMl = typeItems.reduce((s, i) => s + (i.payload.amountMl ?? 0), 0);
        label = totalMl > 0 ? `${count}× · ${displayVolume(totalMl, unitSystem)}` : `${count}×`;
        break;
      }
      case "weight":
        label = typeItems[0].payload.weightG ? displayWeight(typeItems[0].payload.weightG, unitSystem) : `${count}×`;
        break;
      default:
        label = `${count}×`;
    }
    result.push({ type, label });
  }
  return result;
}

export function shortDate(dateKey: string): string {
  const d = new Date(dateKey + "T00:00:00");
  return d.toLocaleDateString([i18n.language], { month: "short", day: "numeric" });
}

export function dateLabel(dateKey: string): string {
  const today     = toDateKey(new Date().toISOString());
  const yesterday = toDateKey(new Date(Date.now() - 86400000).toISOString());
  if (dateKey === today)     return i18n.t("common.today");
  if (dateKey === yesterday) return i18n.t("common.yesterday");
  const d = new Date(dateKey + "T00:00:00");
  return d.toLocaleDateString([i18n.language], { weekday: "short", month: "short", day: "numeric" });
}

export function buildHealthData(logs: LogEvent[], days: string[]) {
  const map = new Map(days.map((d) => [d, { date: d, vomiting: 0, hiccup: 0, fever: 0, pain: 0 }]));
  for (const log of logs) {
    const d = toDateKey(log.occurredAt);
    const row = map.get(d);
    if (!row) continue;
    if (log.type === "vomiting") row.vomiting++;
    else if (log.type === "hiccup") row.hiccup++;
    else if (log.type === "fever") row.fever++;
    else if (log.type === "pain") row.pain++;
  }
  return [...map.values()];
}

export function buildSleepData(logs: LogEvent[], days: string[], cutoffMinutes = 0) {
  const map = new Map(days.map((d) => [d, { date: d, sleepCount: 0, sleepDurationMin: 0 }]));
  for (const log of logs) {
    if (log.type !== "sleep") continue;
    const startRow = map.get(toSleepDayKey(log.occurredAt, cutoffMinutes));
    if (startRow) startRow.sleepCount++;
    if (log.payload.endedAt) {
      for (const segment of splitMinutesByDay(log.occurredAt, log.payload.endedAt, cutoffMinutes)) {
        const row = map.get(segment.date);
        if (row) row.sleepDurationMin += segment.minutes;
      }
    } else if (startRow) {
      startRow.sleepDurationMin += log.payload.durationMin ?? 0;
    }
  }
  return [...map.values()];
}

export function buildNursingData(logs: LogEvent[], days: string[]) {
  const map = new Map(days.map((d) => [d, { date: d, left: 0, right: 0 }]));
  for (const log of logs) {
    if (log.type !== "feed" || log.payload.feedMethod !== "nursing") continue;
    const d = toDateKey(log.occurredAt);
    const row = map.get(d);
    if (!row) continue;
    const side = log.payload.nursingSide;
    if (!side || side === "left" || side === "both") row.left++;
    if (side === "right" || side === "both") row.right++;
  }
  return [...map.values()];
}
